import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import Modal from "../Modal/Modal";
import ConfirmDialog from "../components/ConfirmDialog";
import { useNotification } from "../../../hooks/useNotification";
import {
  getAdminMmTaskTypes,
  createAdminMmTaskType,
  updateAdminMmTaskType,
  deleteAdminMmTaskType,
} from "../../../services/api";
import "./MmTaskTypesModal.css";

const EMPTY_FORM = {
  name: "",
  code: "",
  is_active: true,
};

/**
 * Admin management for Merchandising (MM) task types.
 * Mirrors the display proof type manager: create, rename, enable/disable, and
 * delete (task types already used by a session are archived, not removed).
 */
export default function MmTaskTypesModal({ isOpen, onClose, canEdit = false }) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();

  const [taskTypes, setTaskTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingTypeId, setDeletingTypeId] = useState(null);
  const [togglingTypeId, setTogglingTypeId] = useState(null);
  const [pendingDeleteType, setPendingDeleteType] = useState(null);
  const savingLockRef = useRef(false);

  const isEditing = Boolean(editingTypeId);

  const fetchTaskTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAdminMmTaskTypes({ includeInactive: true });
      setTaskTypes(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      setTaskTypes([]);
      showError(
        error?.response?.data?.error ||
          t("admin.mmTasks.messages.loadFailed", {
            defaultValue: "Failed to load MM task types",
          }),
      );
    } finally {
      setLoading(false);
    }
  }, [showError, t]);

  useEffect(() => {
    if (!isOpen) return;
    fetchTaskTypes();
  }, [isOpen, fetchTaskTypes]);

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setEditingTypeId(null);
  }, []);

  const handleClose = () => {
    if (saving || deletingTypeId) return;
    resetForm();
    onClose();
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEdit = (taskType) => {
    setEditingTypeId(taskType.mm_task_type_id);
    setForm({
      name: taskType?.name || "",
      code: taskType?.code || "",
      is_active: Boolean(taskType?.is_active),
    });
  };

  const handleSave = async (event) => {
    event.preventDefault();
    if (savingLockRef.current) return;

    const trimmedName = String(form.name || "").trim();
    if (!trimmedName) {
      showError(
        t("admin.mmTasks.validation.nameRequired", {
          defaultValue: "Task name is required",
        }),
      );
      return;
    }

    const payload = {
      name: trimmedName,
      is_active: Boolean(form.is_active),
    };

    const trimmedCode = String(form.code || "").trim();
    if (!editingTypeId && trimmedCode) {
      payload.code = trimmedCode;
    }

    try {
      savingLockRef.current = true;
      setSaving(true);

      if (editingTypeId) {
        await updateAdminMmTaskType(editingTypeId, payload);
        showSuccess(
          t("admin.mmTasks.messages.updated", {
            defaultValue: "MM task type updated",
          }),
        );
      } else {
        await createAdminMmTaskType(payload);
        showSuccess(
          t("admin.mmTasks.messages.created", {
            defaultValue: "MM task type created",
          }),
        );
      }

      await fetchTaskTypes();
      resetForm();
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.mmTasks.messages.saveFailed", {
            defaultValue: "Failed to save MM task type",
          }),
      );
    } finally {
      savingLockRef.current = false;
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteType) return;
    try {
      setDeletingTypeId(pendingDeleteType.mm_task_type_id);
      const response = await deleteAdminMmTaskType(
        pendingDeleteType.mm_task_type_id,
      );
      // A returned record means it was archived because sessions reference it.
      showSuccess(
        response?.data
          ? t("admin.mmTasks.messages.archived", {
              defaultValue: "MM task type archived (already used by sessions)",
            })
          : t("admin.mmTasks.messages.deleted", {
              defaultValue: "MM task type deleted",
            }),
      );

      if (editingTypeId === pendingDeleteType.mm_task_type_id) {
        resetForm();
      }

      setPendingDeleteType(null);
      await fetchTaskTypes();
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.mmTasks.messages.deleteFailed", {
            defaultValue: "Failed to delete MM task type",
          }),
      );
    } finally {
      setDeletingTypeId(null);
    }
  };

  const handleToggleStatus = async (taskType) => {
    try {
      setTogglingTypeId(taskType.mm_task_type_id);
      await updateAdminMmTaskType(taskType.mm_task_type_id, {
        is_active: !taskType.is_active,
      });
      showSuccess(
        t("admin.mmTasks.messages.updated", {
          defaultValue: "MM task type updated",
        }),
      );
      await fetchTaskTypes();
    } catch (error) {
      showError(
        error?.response?.data?.error ||
          t("admin.mmTasks.messages.saveFailed", {
            defaultValue: "Failed to save MM task type",
          }),
      );
    } finally {
      setTogglingTypeId(null);
    }
  };

  const submitLabel = saving
    ? t("admin.common.loading")
    : isEditing
      ? t("admin.mmTasks.updateTask", { defaultValue: "Update Task" })
      : t("admin.mmTasks.addTask", { defaultValue: "Add Task" });

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size="large">
        <div className="admin-mm-tasks-modal">
          <div className="admin-mm-tasks-header">
            <h3 className="admin-mm-tasks-title">
              {t("admin.mmTasks.title", { defaultValue: "Manage MM Tasks" })}
            </h3>
            <p className="admin-mm-tasks-subtitle">
              {t("admin.mmTasks.subtitle", {
                defaultValue:
                  "Task types merchandisers choose when checking in.",
              })}
            </p>
          </div>

          {canEdit ? (
            <form className="admin-mm-tasks-form" onSubmit={handleSave}>
              <div className="admin-mm-tasks-form-grid">
                <label className="admin-mm-tasks-field">
                  <span>{t("admin.mmTasks.fields.name", { defaultValue: "Name" })}</span>
                  <input
                    type="text"
                    name="name"
                    className="admin-mm-tasks-input"
                    value={form.name}
                    onChange={handleInputChange}
                    maxLength={120}
                    disabled={saving}
                  />
                </label>

                <label className="admin-mm-tasks-field">
                  <span>{t("admin.mmTasks.fields.code", { defaultValue: "Code" })}</span>
                  <input
                    type="text"
                    name="code"
                    className="admin-mm-tasks-input"
                    value={form.code}
                    onChange={handleInputChange}
                    placeholder={t("admin.mmTasks.codePlaceholder", {
                      defaultValue: "Auto-generated from name",
                    })}
                    maxLength={50}
                    disabled={saving || isEditing}
                  />
                </label>
              </div>

              <div className="admin-mm-tasks-toolbar">
                <label className="admin-mm-tasks-checkbox">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleInputChange}
                    disabled={saving}
                  />
                  <span>
                    {form.is_active
                      ? t("admin.mmTasks.status.active", { defaultValue: "Active" })
                      : t("admin.mmTasks.status.inactive", { defaultValue: "Inactive" })}
                  </span>
                </label>

                <div className="admin-mm-tasks-form-actions">
                  {isEditing ? (
                    <button
                      type="button"
                      className="admin-btn-secondary"
                      onClick={resetForm}
                      disabled={saving}
                    >
                      {t("admin.common.cancel")}
                    </button>
                  ) : null}
                  <button
                    type="submit"
                    className="admin-btn-primary admin-mm-tasks-submit-btn"
                    disabled={saving}
                    aria-busy={saving}
                  >
                    {saving ? (
                      <Loader2 size={14} className="admin-mm-tasks-spin" />
                    ) : isEditing ? (
                      <Pencil size={14} />
                    ) : (
                      <Plus size={14} />
                    )}
                    {submitLabel}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <p className="admin-mm-tasks-readonly-note">
              {t("admin.mmTasks.readOnly", {
                defaultValue: "Only super admins can add or change MM task types.",
              })}
            </p>
          )}

          <div className="admin-mm-tasks-list-wrapper">
            {loading ? (
              <p className="admin-mm-tasks-empty">{t("admin.common.loading")}</p>
            ) : taskTypes.length === 0 ? (
              <p className="admin-mm-tasks-empty">
                {t("admin.mmTasks.empty", { defaultValue: "No MM task types yet." })}
              </p>
            ) : (
              <table className="admin-mm-tasks-table">
                <thead>
                  <tr>
                    <th>{t("admin.mmTasks.fields.name", { defaultValue: "Name" })}</th>
                    <th>{t("admin.mmTasks.fields.code", { defaultValue: "Code" })}</th>
                    <th>{t("admin.mmTasks.fields.status", { defaultValue: "Status" })}</th>
                    {canEdit ? (
                      <th>{t("admin.users.actions")}</th>
                    ) : null}
                  </tr>
                </thead>
                <tbody>
                  {taskTypes.map((taskType) => {
                    const isDeleting = deletingTypeId === taskType.mm_task_type_id;
                    const isToggling = togglingTypeId === taskType.mm_task_type_id;
                    return (
                      <tr key={taskType.mm_task_type_id || taskType.code}>
                        <td>{taskType.name}</td>
                        <td>
                          <code>{taskType.code}</code>
                        </td>
                        <td>
                          <button
                            type="button"
                            className={`admin-mm-tasks-row-switch ${
                              taskType.is_active ? "is-on" : "is-off"
                            }`}
                            onClick={() => handleToggleStatus(taskType)}
                            disabled={!canEdit || saving || isDeleting || isToggling}
                          >
                            <span className="admin-mm-tasks-row-switch-track">
                              <span className="admin-mm-tasks-row-switch-knob" />
                            </span>
                            <span className="admin-mm-tasks-row-switch-label">
                              {taskType.is_active
                                ? t("admin.mmTasks.status.active", { defaultValue: "Active" })
                                : t("admin.mmTasks.status.inactive", { defaultValue: "Inactive" })}
                            </span>
                          </button>
                        </td>
                        {canEdit ? (
                          <td>
                            <div className="admin-mm-tasks-actions">
                              <button
                                type="button"
                                className="admin-mm-tasks-edit-btn"
                                onClick={() => handleEdit(taskType)}
                                disabled={saving || isDeleting}
                              >
                                <Pencil size={14} />
                                {t("admin.common.edit")}
                              </button>
                              <button
                                type="button"
                                className="admin-mm-tasks-delete-btn"
                                onClick={() => setPendingDeleteType(taskType)}
                                disabled={saving || isDeleting}
                              >
                                <Trash2 size={14} />
                                {isDeleting
                                  ? t("admin.common.loading")
                                  : t("admin.common.delete")}
                              </button>
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={Boolean(pendingDeleteType)}
        onClose={() => {
          if (deletingTypeId) return;
          setPendingDeleteType(null);
        }}
        size="small"
      >
        <ConfirmDialog
          title={t("admin.mmTasks.deleteTitle", {
            defaultValue: "Delete MM task type",
          })}
          message={t("admin.mmTasks.confirmDelete", {
            name: pendingDeleteType?.name || pendingDeleteType?.code || "",
            defaultValue:
              "Delete this MM task type? If sessions already use it, it will be archived instead.",
          })}
          variant="danger"
          confirmText={t("admin.common.delete")}
          cancelText={t("admin.common.cancel")}
          processingText={t("admin.common.processing")}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            if (deletingTypeId) return;
            setPendingDeleteType(null);
          }}
          isLoading={Boolean(deletingTypeId)}
        />
      </Modal>
    </>
  );
}
